import { useMemo, useState } from 'react'
import { View, Text } from 'react-native'
import { AppModal } from '@/components/ui/AppModal'
import { GlassSheet } from '@/components/ui/GlassSheet'
import { ScrollView } from 'react-native-gesture-handler'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import i18n from '@/lib/i18n/config'
import Constants from 'expo-constants'
import { StatusBar } from 'expo-status-bar'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useScreenInsets } from '@/lib/hooks/use-screen-insets'
import Animated from 'react-native-reanimated'
import {
  UserPen,
  ShieldCheck,
  CreditCard,
  Bell,
  Shield,
  Settings,
  LogOut,
  ChevronRight,
  BadgeCheck,
  Building2,
  FileText,
  CalendarX,
  Camera,
  Trash2,
  Download,
} from '@/components/ui/icons'
import { Avatar } from '@/components/ui/Avatar'
import { Pressable } from '@/components/ui/Pressable'
import { TrackedNativePressable as ModalPressable } from '@/components/ui/TrackedNativePressable'
import { Rating } from '@/components/ui/Rating'
import { ProfileCompletionCard } from '@/components/ui/ProfileCompletionCard'
import { useAuth } from '@/lib/hooks/use-auth'
import { useProfile } from '@/lib/hooks/use-profile'
import { useNotificationsUnreadCount } from '@/lib/hooks/use-notifications-unread'
import { useSemanticColors, RADIUS, SPACING } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { getCurrentProfileDisplay } from '@/lib/utils/profile-display'
import { formatBookingMonthYear } from '@/lib/utils/dates'
import { WEB_SHADOWS, webShadow } from '@/lib/theme/web-shadow'
import { useTheme } from '@/lib/hooks/use-theme'
import { useEntrance } from '@/lib/hooks/use-entrance'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { CountBadge } from '@/components/ui/CountBadge'

// ─── Menu Item ──────────────────────────────────────────────────────────────

function MenuItem({
  icon: IconComponent,
  label,
  subtitle,
  onPress,
  danger,
  colors,
  testID,
  badgeCount,
}: {
  icon: typeof UserPen
  label: string
  /**
   * Optional second line for rows whose purpose is not self-evident from the
   * label (e.g. the data-export row). Also surfaced as the accessibility hint.
   */
  subtitle?: string
  onPress: () => void
  danger?: boolean
  colors: ReturnType<typeof useSemanticColors>
  testID?: string
  badgeCount?: number
}) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityHint={subtitle}
      testID={testID}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        minHeight: 52,
        paddingVertical: SPACING.sm,
        paddingHorizontal: SPACING.md,
        borderRadius: RADIUS.md,
        opacity: pressed ? 0.72 : 1,
      })}
    >
      <IconComponent
        size={20}
        color={danger ? COLORS.coral.DEFAULT : colors.textSecondary}
        strokeWidth={1.8}
      />
      <View style={{ flex: 1, marginLeft: SPACING.md }}>
        <Text
          style={{
            fontSize: 15,
            fontFamily: FONTS.body,
            color: danger ? colors.textAccent : colors.textPrimary,
          }}
        >
          {label}
        </Text>
        {subtitle ? (
          <Text
            style={{
              marginTop: SPACING.xs / 2,
              fontSize: 12,
              lineHeight: 16,
              fontFamily: FONTS.body,
              color: colors.textMuted,
            }}
          >
            {subtitle}
          </Text>
        ) : null}
      </View>
      <View style={{ marginRight: SPACING.xs }}>
        <CountBadge count={badgeCount ?? 0} />
      </View>
      {!danger && <ChevronRight size={18} color={colors.textSecondary} strokeWidth={2} />}
    </Pressable>
  )
}

// ─── Divider ────────────────────────────────────────────────────────────────

function Divider({ colors }: { colors: ReturnType<typeof useSemanticColors> }) {
  return (
    <View style={{ height: 1, backgroundColor: colors.border, marginHorizontal: SPACING.md }} />
  )
}

function LogoutDialogAction({
  label,
  onPress,
  variant,
  colors,
  testID,
}: {
  label: string
  onPress: () => void
  variant: 'primary' | 'secondary'
  colors: ReturnType<typeof useSemanticColors>
  testID: string
}) {
  const isPrimary = variant === 'primary'

  return (
    <ModalPressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      analyticsId={testID}
      analyticsLabel={label}
      testID={testID}
      hitSlop={8}
      android_ripple={{
        color: isPrimary ? 'rgba(255,255,255,0.18)' : `${colors.textAccent}12`,
        borderless: false,
      }}
      style={{
        minHeight: 52,
        width: '100%',
        borderRadius: RADIUS.lg,
        borderWidth: isPrimary ? 0 : 1,
        borderColor: colors.border,
        backgroundColor: isPrimary ? COLORS.coral.DEFAULT : colors.surfaceSubtle,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: SPACING.lg,
      }}
    >
      <Text
        style={{
          fontSize: 15,
          fontFamily: FONTS.bodyBold,
          color: isPrimary ? '#FFFFFF' : colors.textPrimary,
          textAlign: 'center',
        }}
        numberOfLines={1}
        minimumFontScale={0.78}
      >
        {label}
      </Text>
    </ModalPressable>
  )
}

// ─── Profile Screen ─────────────────────────────────────────────────────────

export default function ProfileScreen() {
  const { t } = useTranslation()
  const enter = useEntrance()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  // Two different questions, two different answers. `windowInsets` is the
  // physical unsafe area — what the logout modal needs, because a Modal is its
  // own window and really does reach the screen edge. `insets` is what this
  // SCREEN still owes after the tab bar has paid its share.
  const windowInsets = useSafeAreaInsets()
  const insets = useScreenInsets()
  const { isDesktop } = useBreakpoint()
  const { signOut, user } = useAuth()
  const { profile, completion } = useProfile()
  const unreadNotifications = useNotificationsUnreadCount()
  const profileDisplay = useMemo(() => getCurrentProfileDisplay({ profile, user }), [profile, user])

  const [showLogoutModal, setShowLogoutModal] = useState(false)

  const handleLogout = () => {
    impact(ImpactStyle.Heavy)
    setShowLogoutModal(true)
  }

  const memberSince = profile?.createdAt
    ? formatBookingMonthYear(profile.createdAt, i18n.language, 'long')
    : ''

  return (
    <View style={{ flex: 1, backgroundColor: colors.background }}>
      <StatusBar style={isDesktop ? (isDark ? 'light' : 'dark') : 'light'} />
      <ScreenContainer variant="content" scroll={false}>
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            paddingBottom: insets.bottom + SPACING.lg,
          }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          <View
            style={
              isDesktop
                ? {
                    backgroundColor: colors.surface,
                    borderRadius: RADIUS.xl,
                    overflow: 'hidden',
                    ...WEB_SHADOWS.lg,
                  }
                : {}
            }
          >
            {/* ── Solid Brand Header ── */}
            <View
              testID="profile.header"
              style={{
                backgroundColor: COLORS.purple.DEFAULT,
                // No `insets.top` here: ScreenContainer already pads the safe
                // area above this header. Adding it again is what put a band of
                // background between the status bar and the purple header —
                // the same double-count as the dead band above the tab bar,
                // on the other edge. (Its siblings in trips/, messages/ and
                // hosting/ carry this same note; this one had drifted.)
                paddingTop: isDesktop ? SPACING.xl : SPACING.md,
                paddingBottom: SPACING.xl,
                paddingHorizontal: SPACING.lg,
                alignItems: 'center',
              }}
            >
              {/* Avatar — prevents Android view flattening */}
              <Animated.View collapsable={false} entering={enter(1)}>
                <Pressable
                  onPress={() => router.push('/profile/edit-profile')}
                  accessibilityRole="button"
                  accessibilityLabel={t('profile.change_avatar')}
                  testID="profile.avatar-button"
                  style={{ position: 'relative', width: 86, height: 86 }}
                >
                  <View
                    style={{
                      width: 86,
                      height: 86,
                      borderRadius: 43, // circle = width / 2
                      borderWidth: 3,
                      borderColor: '#FFFFFF',
                      ...webShadow({ y: 2, blur: 8, opacity: 0.15, elevation: 4 }),
                    }}
                  >
                    <View style={{ width: 80, height: 80, borderRadius: 40, overflow: 'hidden' }}>
                      <Avatar
                        size="xl"
                        source={profileDisplay.avatarUrl}
                        blurhash={profileDisplay.avatarBlurhash ?? undefined}
                        fallback={profileDisplay.fallback}
                        colorSeed={profileDisplay.id}
                        accessibilityLabel={profileDisplay.accessibilityLabel}
                      />
                    </View>
                  </View>
                  <View
                    style={{
                      position: 'absolute',
                      bottom: -2,
                      right: -2,
                      zIndex: 10,
                      elevation: 10,
                      width: 28,
                      height: 28,
                      borderRadius: RADIUS.md + 2, // circle = 28 / 2
                      backgroundColor: COLORS.coral.dark,
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderWidth: 2,
                      borderColor: '#FFFFFF',
                    }}
                  >
                    <Camera size={14} color="#FFFFFF" strokeWidth={2} />
                  </View>
                </Pressable>
              </Animated.View>

              {/* Name + Email */}
              <Animated.View
                collapsable={false}
                entering={enter(2)}
                style={{ alignItems: 'center', marginTop: SPACING.md - RADIUS.xs }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text
                    style={{
                      fontSize: 22,
                      fontFamily: FONTS.bodyBold,
                      color: '#FFFFFF',
                    }}
                    numberOfLines={1}
                  >
                    {profileDisplay.displayName}
                  </Text>
                  {profile?.isVerified && (
                    <BadgeCheck
                      size={20}
                      color="#FFFFFF"
                      fill={COLORS.coral.DEFAULT}
                      strokeWidth={2}
                      style={{ marginLeft: SPACING.xs + 2 }}
                    />
                  )}
                </View>
                <Text
                  style={{
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: 'rgba(255,255,255,0.90)',
                    marginTop: SPACING.xs,
                  }}
                  numberOfLines={1}
                >
                  {profileDisplay.email ?? ''}
                </Text>
              </Animated.View>

              {/* Rating + Member Since */}
              <Animated.View
                collapsable={false}
                entering={enter(4)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: SPACING.sm + SPACING.xs,
                  gap: SPACING.md,
                }}
              >
                {profile?.guestRatingAvg != null && profile.guestRatingAvg > 0 && (
                  <Rating
                    value={profile.guestRatingAvg}
                    size={14}
                    showLabel
                    count={profile.guestRatingCount}
                  />
                )}
                {memberSince ? (
                  <Text
                    style={{
                      fontSize: 12,
                      fontFamily: FONTS.body,
                      color: 'rgba(255,255,255,0.85)',
                    }}
                  >
                    {t('profile.member_since', { date: memberSince })}
                  </Text>
                ) : null}
              </Animated.View>
            </View>

            {/* ── Profile Completion ── */}
            {completion.percent < 100 ? (
              <Animated.View
                collapsable={false}
                entering={enter(5)}
                style={{
                  marginHorizontal: SPACING.md,
                  marginTop: SPACING.md,
                  backgroundColor: colors.surface,
                  borderRadius: RADIUS.lg,
                  ...WEB_SHADOWS.sm,
                }}
              >
                <ProfileCompletionCard completion={completion} testID="profile.completion-card" />
              </Animated.View>
            ) : null}

            {/* ── Menu — is critical for Android touch ── */}
            <Animated.View
              collapsable={false}
              entering={enter(6)}
              style={{
                marginHorizontal: SPACING.md,
                marginTop: SPACING.md,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                ...WEB_SHADOWS.sm,
              }}
            >
              <MenuItem
                icon={UserPen}
                label={t('profile.edit_profile')}
                onPress={() => router.push('/profile/edit-profile')}
                colors={colors}
                testID="profile.edit-profile"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={ShieldCheck}
                label={t('profile.verification')}
                onPress={() => router.push('/profile/verification')}
                colors={colors}
                testID="profile.verification"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={CreditCard}
                label={t('profile.payment_methods')}
                onPress={() => router.push('/profile/payment-methods')}
                colors={colors}
                testID="profile.payment-methods"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={Bell}
                label={t('profile.notifications')}
                onPress={() => router.push('/profile/notifications')}
                colors={colors}
                testID="profile.notifications"
                badgeCount={unreadNotifications}
              />
              <Divider colors={colors} />
              <MenuItem
                icon={Shield}
                label={t('profile.privacy')}
                onPress={() => router.push('/profile/privacy')}
                colors={colors}
                testID="profile.privacy"
              />
              <Divider colors={colors} />
              {/* D.L. 144/2024 — Acceso + Portabilidad. Without this row the
                  data-export screen has no navigation entry point at all, so
                  the two rights cannot be exercised from the app. Kept next to
                  Privacidad and in the same menu as Eliminar cuenta
                  (Cancelación) so all data-subject rights live together. */}
              <MenuItem
                icon={Download}
                label={t('profile.data_export')}
                subtitle={t('profile.data_export_subtitle')}
                onPress={() => router.push('/profile/data-export' as never)}
                colors={colors}
                testID="profile.data-export"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={FileText}
                label={t('profile.terms_of_service')}
                onPress={() => router.push('/profile/terms' as never)}
                colors={colors}
                testID="profile.terms"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={CalendarX}
                label={t('profile.cancellation_policy')}
                onPress={() => router.push('/profile/cancellation' as never)}
                colors={colors}
                testID="profile.cancellation"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={Settings}
                label={t('profile.settings')}
                onPress={() => router.push('/profile/settings')}
                colors={colors}
                testID="profile.settings"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={Building2}
                label={t('profile.imprint')}
                onPress={() => router.push('/profile/imprint' as never)}
                colors={colors}
                testID="profile.imprint"
              />
            </Animated.View>

            {/* ── Logout + delete account ── */}
            <Animated.View
              collapsable={false}
              entering={enter(7)}
              style={{
                marginHorizontal: SPACING.md,
                marginTop: SPACING.lg,
                marginBottom: SPACING.md,
                backgroundColor: colors.surface,
                borderRadius: RADIUS.lg,
                ...WEB_SHADOWS.sm,
              }}
            >
              <MenuItem
                icon={LogOut}
                label={t('profile.logout')}
                onPress={handleLogout}
                danger
                colors={colors}
                testID="profile.logout-button"
              />
              <Divider colors={colors} />
              <MenuItem
                icon={Trash2}
                label={t('compliance.delete_title')}
                onPress={() => router.push('/profile/delete-account' as never)}
                danger
                colors={colors}
                testID="profile.delete-account-button"
              />
            </Animated.View>

            {/* App version footer — investors and support always check this. */}
            <Text
              style={{
                fontSize: 12,
                fontFamily: FONTS.body,
                color: colors.textMuted,
                textAlign: 'center',
                marginTop: SPACING.sm,
                marginBottom: SPACING.md,
              }}
              testID="profile.app-version"
            >
              {t('profile.version', {
                version: (Constants.expoConfig?.version as string | undefined) ?? 'dev',
              })}
            </Text>
          </View>
        </ScrollView>
      </ScreenContainer>

      {/* Logout confirmation modal */}
      {showLogoutModal && (
        <AppModal
          visible={showLogoutModal}
          transparent
          animationType="fade"
          onRequestClose={() => setShowLogoutModal(false)}
          statusBarTranslucent
        >
          <View
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              padding: SPACING.lg,
              paddingTop: windowInsets.top + SPACING.lg,
              paddingBottom: windowInsets.bottom + SPACING.lg,
            }}
          >
            <GlassSheet
              accessibilityViewIsModal
              accessibilityLabel={t('profile.logout_confirm_title')}
              style={{
                borderRadius: RADIUS.xl,
                maxWidth: 400,
                width: '100%',
              }}
              contentStyle={{
                paddingHorizontal: SPACING.lg,
                paddingTop: SPACING.xl,
                paddingBottom: SPACING.lg,
                gap: SPACING.lg,
              }}
            >
              <Text
                style={{
                  fontSize: 18,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                  textAlign: 'center',
                }}
              >
                {t('profile.logout_confirm_title')}
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  textAlign: 'center',
                  lineHeight: 20,
                }}
              >
                {t('profile.logout_confirm_message')}
              </Text>
              <View
                style={{
                  width: '100%',
                  flexDirection: 'column',
                  gap: SPACING.sm,
                }}
                testID="profile.logout-modal-actions"
              >
                <LogoutDialogAction
                  label={t('profile.logout')}
                  onPress={() => {
                    setShowLogoutModal(false)
                    void signOut()
                  }}
                  variant="primary"
                  colors={colors}
                  testID="profile.logout-modal-confirm"
                />
                <LogoutDialogAction
                  label={t('common.cancel')}
                  onPress={() => setShowLogoutModal(false)}
                  variant="secondary"
                  colors={colors}
                  testID="profile.logout-modal-cancel"
                />
              </View>
            </GlassSheet>
          </View>
        </AppModal>
      )}
    </View>
  )
}
